import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { CompletionNoteSheet } from '@/components/CompletionNoteSheet';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { taskKeys } from './useTasks';

interface AskOptions {
  /** Runs once the prompt closes, saved or skipped. Lets a caller queue what comes next. */
  onDone?: () => void;
}

type Ask = (taskId: number, title: string | null, options?: AskOptions) => void;

/**
 * No-op default so a component rendered outside the provider (a test, a
 * standalone story) completes tasks without exploding.
 */
const CompletionNoteContext = createContext<Ask>(() => {});

export function useCompletionNote(): Ask {
  return useContext(CompletionNoteContext);
}

interface Pending {
  taskId: number;
  title: string | null;
}

/**
 * One completion-note prompt for the whole app.
 *
 * Every way of finishing a task -- the timer, the agenda tick box, the status
 * field in the editor -- asks the same question in the same place, rather than
 * each surface growing its own sheet and its own copy.
 */
export function CompletionNoteProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const [saving, setSaving] = useState(false);
  const onDone = useRef<(() => void) | undefined>(undefined);

  const ask = useCallback<Ask>((taskId, title, options) => {
    onDone.current = options?.onDone;
    setPending({ taskId, title });
  }, []);

  const close = useCallback(() => {
    setPending(null);
    setSaving(false);
    const next = onDone.current;
    onDone.current = undefined;
    next?.();
  }, []);

  const save = useCallback(
    async (text: string) => {
      if (!pending) return;
      setSaving(true);
      try {
        await api(`/api/tasks/${pending.taskId}/notes`, {
          method: 'POST',
          json: { text },
        });
        void qc.invalidateQueries({ queryKey: taskKeys.all });
        toast.show('Note saved');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not save the note');
      }
      close();
    },
    [pending, qc, close],
  );

  return (
    <CompletionNoteContext.Provider value={ask}>
      {children}
      <CompletionNoteSheet
        open={pending !== null}
        taskTitle={pending?.title ?? null}
        saving={saving}
        onSave={(text) => void save(text)}
        onSkip={close}
      />
    </CompletionNoteContext.Provider>
  );
}
